import {
  createContext,
  useContext,
  useId,
  useState,
  type ComponentType,
} from "react";
import { createPortal } from "react-dom";
import type { Code } from "mdast";
import { spliceFence, canSplice, refOf } from "./parseFence.js";
import { EmbedModal } from "./EmbedModal.js";
import {
  EmbedIdContext,
  EmbedReportContext,
  type EmbedResultMeta,
} from "../data/embedId.js";
import type { VizProps, VizEditorProps } from "../viz/registry.js";
import { EDIT_SLOT_ID } from "../layout/RightSidebar.js";

/** Hover title for an embed's rendered result: freshness + when it was fetched.
 * Undefined (no title) until a result has reported in. */
function ageTitle(m: EmbedResultMeta | null): string | undefined {
  if (!m?.cachedAt) return undefined;
  const state = m.source === "live" ? "live" : m.stale ? "stale, refreshing" : "cached";
  return `${state} — as of ${new Date(m.cachedAt).toLocaleString()}`;
}

/** Fence kinds whose body is SQL. An inline (non-`@ref`) SQL body is the shape
 * we want to discourage in favour of a referenced `.sql` file, so we flag it in
 * the corner chip. (`html` is the only non-SQL viz.) */
const SQL_KINDS = new Set([
  "query",
  "metric",
  "table",
  "bar",
  "line",
  "waterfall",
  "funnel",
]);

/**
 * Editing context for embeds. Present (interactive) only when Markdown is
 * given an onSourceChange callback — i.e. it's rendering an editable draft.
 * Otherwise EmbedFrame renders a plain view and adds no chrome.
 */
interface EmbedCtx {
  interactive: boolean;
  source: string;
  onSourceChange: (next: string) => void;
  selected: number | null;
  setSelected: (i: number | null) => void;
  /**
   * Stable per-render index for each selectable node (embed code nodes and
   * `:::sidebyside` directives), in document order. Keyed by node identity, so
   * indices survive a re-parse even as byte offsets shift.
   */
  nodeIndex: Map<object, number>;
  /** Content repo — for the SQL editor's query picker / resolved-path links. */
  repo: string;
  /** Repo path of the page — base for resolving a reference embed's `@ref`. */
  path: string;
  /** Open the page editor (double-click on prose). Absent = no editor to open. */
  onRequestEdit?: () => void;
}

export const ctxDefault: EmbedCtx = {
  interactive: false,
  source: "",
  onSourceChange: () => {},
  selected: null,
  setSelected: () => {},
  nodeIndex: new Map(),
  repo: "",
  path: "",
};

export const EmbedContext = createContext<EmbedCtx>(ctxDefault);

// The modal (and the chrome hook editors use to take over its header) moved to
// EmbedModal.tsx so the rich editor can open the same one; re-exported here
// because every embed editor imports it from this module.
export { ModalChromeContext, type ModalChrome } from "./EmbedModal.js";

interface EmbedFrameProps {
  node: Code;
  kind: string;
  attrs: Record<string, string>;
  source: string; // fence body
  View: ComponentType<VizProps>;
  editor?: ComponentType<VizEditorProps>;
  label?: string;
}

export function EmbedFrame({
  node,
  kind,
  attrs,
  source,
  View,
  editor,
  label,
}: EmbedFrameProps) {
  const ctx = useContext(EmbedContext);
  const [editing, setEditing] = useState(false);
  // Stable DOM id for this embed; its queries carry it (via EmbedIdContext →
  // useQuery) so a click in the live-tasks card can scroll back here.
  const embedId = "dw-embed-" + useId().replace(/:/g, "");
  // Result freshness reported up from the viz's useQuery → the hover title.
  const [dataMeta, setDataMeta] = useState<EmbedResultMeta | null>(null);
  const title = ageTitle(dataMeta);
  // Serving a stale cache while a refresh runs — dim the embed with a gray veil.
  const stale = dataMeta?.stale === true;

  if (!ctx.interactive)
    return (
      <EmbedIdContext.Provider value={embedId}>
        <EmbedReportContext.Provider value={setDataMeta}>
          <div id={embedId} title={title} className={stale ? "dw-embed-stale" : undefined}>
            <View attrs={attrs} source={source} />
          </div>
        </EmbedReportContext.Provider>
      </EmbedIdContext.Provider>
    );

  const index = ctx.nodeIndex.get(node) ?? -1;
  const selected = ctx.selected === index;
  const editable = canSplice(ctx.source, node);
  // Inline SQL (a SQL-kind fence whose body isn't a bare `@ref`) — the shape to
  // migrate into a reusable `.sql` file. Flagged in the corner chip.
  const isInlineSql = SQL_KINDS.has(kind) && refOf(source) === null;

  const updateCode = (
    body: string,
    nextAttrs?: Record<string, string>,
  ): void => {
    ctx.onSourceChange(spliceFence(ctx.source, node, body, nextAttrs));
  };

  const slot =
    typeof document !== "undefined"
      ? document.getElementById(EDIT_SLOT_ID)
      : null;

  return (
    <EmbedIdContext.Provider value={embedId}>
    <EmbedReportContext.Provider value={setDataMeta}>
    <div
      id={embedId}
      className={`dw-embed-frame${selected ? " dw-selected" : ""}${stale ? " dw-embed-stale" : ""}`}
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        ctx.setSelected(selected ? null : index);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (editable) setEditing(true);
      }}
    >
      <span className="dw-embed-tag">
        {label ?? kind}
        {isInlineSql && <span className="dw-embed-sql-warn"> · SQL</span>}
        {!editable && " · indented (edit raw)"}
      </span>
      <View attrs={attrs} source={source} />

      {selected &&
        slot &&
        createPortal(
          <section className="dw-card dw-embed-details">
            <header className="dw-card-head">{label ?? kind}</header>
            <div className="dw-card-body">
              <table className="dw-attr-table">
                <tbody>
                  <tr>
                    <td className="dw-muted">kind</td>
                    <td>{kind}</td>
                  </tr>
                  {Object.entries(attrs).map(([k, v]) => (
                    <tr key={k}>
                      <td className="dw-muted">{k}</td>
                      <td>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {editable ? (
                <button onClick={() => setEditing(true)}>edit</button>
              ) : (
                <p className="dw-muted">
                  Indented/quoted fence — edit in the raw textarea.
                </p>
              )}
            </div>
          </section>,
          slot,
        )}

      {editing && (
        <EmbedModal
          label={label ?? kind}
          editor={editor}
          attrs={attrs}
          source={source}
          updateCode={updateCode}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
    </EmbedReportContext.Provider>
    </EmbedIdContext.Provider>
  );
}
